import { useCallback, useEffect, useRef, useState } from "react";
import { AssistClient, hubErrorMessage, type CallerInfo, type UserInfo } from "./realtime/assistClient";
import { CallPeer, readStats, type AnnotationMessage } from "./webrtc/peer";
import { authHeaders, clearSession, loadSession, saveSession, type Session } from "./auth";
import Login from "./Login";
import CallHistory from "./CallHistory";
import Users from "./Users";

/**
 * App web dell'esperto. Fuori chiamata: accesso ed elenco utenti (WEB-01 semplificato). In chiamata: schermata
 * WEB-02 (mockup): strumenti a sinistra (Freccia, Annulla, Cancella, colore), video del visore con la tua camera in
 * basso a destra, pannello Documenti a destra (PDF/immagini trascinati o scelti), barra in basso con microfono,
 * camera e Termina. La diagnostica (statistiche, log) resta in fondo, chiusa di default.
 */

/** Colori delle frecce (nome, esadecimale): vanno al visore nel messaggio "arrow". */
const ARROW_COLORS: { name: string; hex: string }[] = [
  { name: "Azzurro", hex: "#90d0f0" },
  { name: "Giallo", hex: "#ffd166" },
  { name: "Rosso", hex: "#f82838" },
  { name: "Verde", hex: "#58c07a" },
  { name: "Bianco", hex: "#ffffff" },
];

interface ActiveCall { callId: string; peerId: string; peerName: string; iAmCaller: boolean; state: "ringing" | "connecting" | "connected"; connectedAt?: number }
interface Arrow { id: string; x: number; y: number; mine: boolean; color: string; visible?: boolean; tracked?: boolean }
interface VideoStats { width: number; height: number; fps: number; kbps: number; rttMs: number; candidate: string; codec: string; framesDecoded: number; outCodec: string; outKB: number; outFrames: number }
interface SharedDoc { id: string; fileName: string; pageCount: number; status: "uploading" | "ready" | "error"; error?: string }

/** Icone a linea (24×24, currentColor): stile tecnico, coerente con le icone bianche della grafica HoloLens. */
const Icon = {
  arrow: () => <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v13" /><path d="m6 10 6 6 6-6" /><ellipse cx="12" cy="20" rx="7" ry="2" /></svg>,
  undo: () => <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 14 4 9l5-5" /><path d="M4 9h10a6 6 0 0 1 0 12h-3" /></svg>,
  trash: () => <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /></svg>,
  mic: (off: boolean) => <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3M9 21h6" />{off && <path d="M4 4l16 16" />}</svg>,
  cam: (off: boolean) => <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="13" height="10" rx="2" /><path d="m16 10 5-2v8l-5-2" />{off && <path d="M4 4l16 16" />}</svg>,
  hangup: () => <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 13c5-5 13-5 18 0l-2.5 2.5-3-1.5v-2.5a9 9 0 0 0-7 0V14l-3 1.5Z" /></svg>,
  upload: () => <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 16V5" /><path d="m7 10 5-5 5 5" /><path d="M4 19h16" /></svg>,
  rec: () => <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4.5" fill="currentColor" stroke="none" /></svg>,
  expand: () => <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 9V4h5" /><path d="M20 9V4h-5" /><path d="M4 15v5h5" /><path d="M20 15v5h-5" /></svg>,
  compress: () => <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 4v5H4" /><path d="M15 4v5h5" /><path d="M9 20v-5H4" /><path d="M15 20v-5h5" /></svg>,
};

export default function App() {
  // sessione del login (token JWT + utente), ricordata dal browser; null = pagina di accesso
  const [session, setSession] = useState<Session | null>(() => loadSession());
  const [loginNotice, setLoginNotice] = useState<string | undefined>();
  const [showHistory, setShowHistory] = useState(false); // registro "Le mie chiamate" al posto della pagina principale
  const [showUsers, setShowUsers] = useState(false);     // gestione utenti (solo amministratori, 02/11/2026)
  const [connection, setConnection] = useState<"disconnected" | "connecting" | "connected" | "reconnecting">("disconnected");
  const [me, setMe] = useState<{ id: string; name: string; role: string; isAdmin?: boolean } | null>(null);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [incoming, setIncoming] = useState<{ callId: string; from: CallerInfo } | null>(null);
  const [call, setCall] = useState<ActiveCall | null>(null);
  const [peerState, setPeerState] = useState("");
  const [arrows, setArrows] = useState<Arrow[]>([]);
  const [arrowColor, setArrowColor] = useState(ARROW_COLORS[0].hex);
  const [stats, setStats] = useState<VideoStats | null>(null);
  const [clock, setClock] = useState<{ rtt: number; offset: number } | null>(null);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  // registrazione della sessione (video del visore + audio di entrambi), salvata nei download a fine registrazione
  const [recording, setRecording] = useState(false);
  const recorder = useRef<MediaRecorder | null>(null);
  const recStartedAt = useRef(0);
  const recDuration = () => (recStartedAt.current ? (Date.now() - recStartedAt.current) / 1000 : 0);
  const recChunks = useRef<Blob[]>([]);
  const recAudio = useRef<AudioContext | null>(null);
  // resoconto a fine chiamata, mostrato 5 s nell'elenco utenti
  const [ended, setEnded] = useState<{ peerName: string; duration: number; saved: boolean; reason?: string } | null>(null);
  const [netNotice, setNetNotice] = useState<string | null>(null); // server perso durante la chiamata (28/10/2026)
  const retryTimer = useRef<number | null>(null);                   // ritentativo automatico di connessione
  const [recNotice, setRecNotice] = useState<string | null>(null); // esito del salvataggio sul server (arriva dopo la fine)
  useEffect(() => {
    if (!ended) return;
    const t = window.setTimeout(() => setEnded(null), 5000);
    return () => window.clearTimeout(t);
  }, [ended]);

  // schermo intero della schermata di chiamata (pulsante in alto a destra; Esc per uscire)
  const sessionRoot = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else sessionRoot.current?.requestFullscreen().catch(() => {});
  };
  const [logs, setLogs] = useState<string[]>([]);
  const [docs, setDocs] = useState<SharedDoc[]>([]);
  const [dragging, setDragging] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const client = useRef<AssistClient | null>(null);
  const peer = useRef<CallPeer | null>(null);
  const callRef = useRef<ActiveCall | null>(null);
  const localStream = useRef<MediaStream | null>(null);
  const remoteVideo = useRef<HTMLVideoElement>(null);
  const localVideo = useRef<HTMLVideoElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const lastFrame = useRef<{ presented: number; capture?: number }>({ presented: 0 });
  const statsPrev = useRef({ bytes: 0, t: 0 });

  const log = useCallback((m: string) => {
    const line = `${new Date().toLocaleTimeString("it-IT")}  ${m}`;
    console.log("[MDT]", m);
    setLogs((l) => [...l.slice(-199), line]);
  }, []);

  const setCallBoth = (c: ActiveCall | null) => { callRef.current = c; setCall(c); };

  // ---------------- connessione ----------------

  /** Sessione scaduta o non valida: via la sessione, si torna al login con un avviso. */
  function sessionExpired(message: string) {
    clearSession();
    client.current = null;
    setMe(null);
    setConnection("disconnected");
    setLoginNotice(message);
    setSession(null);
  }

  async function connect() {
    const s = session;
    if (!s) return;
    setConnection("connecting");
    const c = new AssistClient("", s.token, {
      connectionChanged: (s) => {
        setConnection(s); log(`server: ${s}`);
        if (s === "connected") { setNetNotice(null); refreshUsers(); }
        if (s === "reconnecting") setNetNotice(callRef.current ? "Connessione al server persa, riprovo… la chiamata continua" : null);
        if (s === "disconnected") {
          // SignalR ha rinunciato (~20 s di tentativi): il server ha già chiuso la chiamata per l'altro partecipante
          setNetNotice(null);
          if (callRef.current) teardown("connessione persa");
          client.current = null;
          scheduleReconnect();
        }
      },
      presenceChanged: (userId, status) => setUsers((us) => us.map((u) => (u.id === userId ? { ...u, status } : u))),
      incomingCall: (callId, from) => { log(`chiamata in arrivo da ${from.name}`); setIncoming({ callId, from }); },
      callAccepted: onCallAccepted,
      callRejected: (_id, reason) => { log(`chiamata rifiutata (${reason})`); teardown(endReasonText(reason)); },
      callEnded: (_id, reason) => { log(`chiamata terminata (${reason})`); teardown(endReasonText(reason)); },
      remoteSdp: onRemoteSdp,
      remoteIce: (_id, cand, mid, idx) => peer.current?.addRemoteIce(cand, mid, idx).catch((e) => log(`ICE: ${e}`)),
      documentAvailable: (_id, d) => log(`documento condiviso dal tecnico: ${d.fileName}`),
    });
    client.current = c; // prima di connect(): gli eventi (es. "connected" → elenco utenti) usano client.current
    try {
      const r = await fetch("/api/me", { headers: authHeaders(s.token) });
      if (r.status === 401) { sessionExpired("La sessione è scaduta: accedi di nuovo"); return; }
      if (!r.ok) throw new Error(`/api/me ha risposto ${r.status}`);
      setMe(await r.json());
      await c.connect();
    } catch (e) {
      log(`connessione fallita: ${hubErrorMessage(e)}`);
      client.current = null;
      setConnection("disconnected");
      scheduleReconnect();
    }
  }

  /** Senza server si riprova da soli ogni 5 s (28/10/2026), finché c'è una sessione; "Riprova" resta per chi non vuole aspettare. */
  function scheduleReconnect() {
    if (retryTimer.current) window.clearTimeout(retryTimer.current);
    retryTimer.current = window.setTimeout(() => {
      retryTimer.current = null;
      if (sessionRef.current && !client.current) void connect();
    }, 5000);
  }
  const sessionRef = useRef(session);
  sessionRef.current = session;

  /** Motivo di fine chiamata del server → testo per la scheda "Chiamata terminata". */
  function endReasonText(reason: string | null | undefined): string | undefined {
    switch (reason) {
      case "disconnected": case "interrupted": return "connessione persa";
      case "timeout": return "nessuna risposta";
      case "rejected": return "rifiutata";
      case "busy": return "utente già in un'altra chiamata";
      case "cancelled": return "annullata";
      default: return undefined;
    }
  }

  // appena c'è una sessione (login o pagina riaperta) ci si collega da soli
  useEffect(() => {
    if (session && !client.current && connection === "disconnected") void connect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  function onLogin(s: Session) {
    saveSession(s);
    setLoginNotice(undefined);
    setSession(s);
  }

  async function logout() {
    await disconnect();
    clearSession();
    setSession(null);
  }

  async function disconnect() {
    if (retryTimer.current) { window.clearTimeout(retryTimer.current); retryTimer.current = null; }
    teardown();
    await client.current?.disconnect();
    client.current = null;
    setMe(null); setUsers([]); setConnection("disconnected");
  }

  async function refreshUsers() {
    // dal browser si vedono e si chiamano solo gli utenti HoloLens (21/10/2026)
    try { setUsers(await client.current!.listUsers("technician")); } catch (e) { log(`elenco utenti: ${hubErrorMessage(e)}`); }
  }

  // ---------------- chiamata ----------------

  async function callUser(u: UserInfo) {
    try {
      const callId = await client.current!.startCall(u.id);
      setCallBoth({ callId, peerId: u.id, peerName: u.name, iAmCaller: true, state: "ringing" });
      log(`chiamo ${u.name}…`);
    } catch (e) { log(`chiamata non avviata: ${hubErrorMessage(e)}`); }
  }

  async function accept() {
    if (!incoming) return;
    const inc = incoming; setIncoming(null);
    setCallBoth({ callId: inc.callId, peerId: inc.from.id, peerName: inc.from.name, iAmCaller: false, state: "connecting" });
    try {
      await setupPeer(inc.callId); // pronto a ricevere l'offerta
      await client.current!.acceptCall(inc.callId);
      log("chiamata accettata, attendo l'offerta");
    } catch (e) { log(`accettazione fallita: ${hubErrorMessage(e)}`); teardown(); }
  }

  async function reject() {
    if (!incoming) return;
    await client.current!.rejectCall(incoming.callId, "rejected").catch(() => {});
    setIncoming(null);
  }

  async function hangUp() {
    const c = callRef.current;
    if (c) await client.current!.endCall(c.callId).catch(() => {});
    teardown();
  }

  async function onCallAccepted(callId: string) {
    const c = callRef.current;
    if (!c || c.callId !== callId) return;
    setCallBoth({ ...c, state: "connecting" });
    if (!c.iAmCaller) return;
    try {
      await setupPeer(callId);
      const sdp = await peer.current!.createOffer();
      await client.current!.sendSdp(callId, "offer", sdp);
      log("offerta inviata");
    } catch (e) { log(`offerta fallita: ${e}`); hangUp(); }
  }

  async function onRemoteSdp(callId: string, type: "offer" | "answer", sdp: string) {
    const p = peer.current;
    if (!p || callRef.current?.callId !== callId) return;
    try {
      if (type === "offer") {
        const answer = await p.acceptOffer(sdp);
        await client.current!.sendSdp(callId, "answer", answer);
        log("offerta ricevuta, risposta inviata");
      } else {
        await p.acceptAnswer(sdp);
        log("risposta ricevuta");
      }
    } catch (e) { log(`SDP: ${e}`); }
  }

  async function setupPeer(callId: string) {
    const ice = await client.current!.getIceServers(callId);
    log(`ICE servers: ${ice.map((s) => s.urls.join(",")).join(" | ")}`);
    const p = new CallPeer(ice, {
      onIceCandidate: (cand, mid, idx) => client.current!.sendIce(callId, cand, mid, idx).catch(() => {}),
      onRemoteStream: (stream) => {
        if (remoteVideo.current) { remoteVideo.current.srcObject = stream; remoteVideo.current.play().catch(() => {}); }
        log(`flusso remoto ricevuto (${stream.getTracks().map((t) => t.kind).join("+")})`);
      },
      onDataOpen: () => log("canale dati aperto"),
      onDataMessage: onAnnotation,
      onStateChange: (s, i) => {
        setPeerState(`${s} / ice ${i}`);
        if (s === "connected") setCallBoth(callRef.current && { ...callRef.current, state: "connected", connectedAt: callRef.current.connectedAt ?? Date.now() });
        if (s === "failed") log("connessione media fallita (serve TURN?)");
      },
      onClockSync: (rtt, offset) => setClock({ rtt, offset: Math.round(offset) }),
    });
    peer.current = p;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: camOn ? { width: 640, height: 480 } : false });
      localStream.current = stream;
      if (localVideo.current) localVideo.current.srcObject = stream;
      stream.getAudioTracks().forEach((t) => (t.enabled = micOn));
      p.addLocalStream(stream);
    } catch (e) {
      log(`microfono/webcam non disponibili (${e}); continuo solo in ricezione`);
      p.pc.addTransceiver("video", { direction: "recvonly" });
      p.pc.addTransceiver("audio", { direction: "recvonly" });
    }
  }

  async function startRecording() {
    const remote = remoteVideo.current?.srcObject as MediaStream | null;
    if (!remote || remote.getVideoTracks().length === 0) { log("registrazione: nessun video del visore da registrare"); return; }
    const token = session?.token ?? "";
    const callId = callRef.current?.callId;
    try {
      const ctx = new AudioContext();
      const dest = ctx.createMediaStreamDestination();
      if (remote.getAudioTracks().length) ctx.createMediaStreamSource(new MediaStream(remote.getAudioTracks())).connect(dest);
      const mic = localStream.current?.getAudioTracks() ?? [];
      if (mic.length) ctx.createMediaStreamSource(new MediaStream(mic)).connect(dest);
      recAudio.current = ctx;
      const stream = new MediaStream([...remote.getVideoTracks(), ...dest.stream.getAudioTracks()]);
      const mime = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"].find((m) => MediaRecorder.isTypeSupported(m)) ?? "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime, videoBitsPerSecond: 2_500_000 } : undefined);
      const peerName = (callRef.current?.peerName ?? "tecnico").replace(/[^\w-]+/g, "_");
      recChunks.current = [];
      setRecNotice(null);

      // registrazione sul server (21/10/2026): il file viene aperto subito e i blocchi caricati mentre si registra,
      // così a fine chiamata è già quasi tutto sul server e si scarica dal registro "Chiamate". Se il server non
      // risponde, il file va nei download come prima.
      const up = { id: null as string | null, pending: [] as Blob[], chain: Promise.resolve(), failed: false, bytes: 0 };
      if (callId) {
        try {
          const r = await fetch(`/api/calls/${callId}/recordings`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ mime: mime || "video/webm" }) });
          if (r.ok) up.id = ((await r.json()) as { id: string }).id; else log(`registrazione sul server rifiutata (${r.status}): salvo nei download`);
        } catch (e) { log(`registrazione sul server non disponibile: salvo nei download (${e})`); }
      }
      const flush = () => {
        if (!up.id || up.failed || up.pending.length === 0) return;
        const blob = new Blob(up.pending.splice(0));
        up.chain = up.chain.then(async () => {
          const r = await fetch(`/api/recordings/${up.id}/chunks`, { method: "PUT", headers: { ...authHeaders(token), "Content-Type": "application/octet-stream" }, body: blob });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          up.bytes = ((await r.json()) as { sizeBytes: number }).sizeBytes;
        }).catch((e) => { if (!up.failed) { up.failed = true; log(`caricamento registrazione interrotto: ${e}; il file andrà nei download`); } });
      };
      const flushTimer = window.setInterval(flush, 5000);

      rec.ondataavailable = (e) => { if (e.data.size > 0) { recChunks.current.push(e.data); if (up.id) up.pending.push(e.data); } };
      rec.onstop = async () => {
        window.clearInterval(flushTimer);
        flush();
        await up.chain;
        const blob = new Blob(recChunks.current, { type: rec.mimeType || "video/webm" });
        recChunks.current = [];
        if (blob.size === 0) return;
        if (up.id && !up.failed) {
          try {
            const r = await fetch(`/api/recordings/${up.id}/finish`, { method: "POST", headers: { ...authHeaders(token), "Content-Type": "application/json" }, body: JSON.stringify({ durationSeconds: Math.round(recDuration()) }) });
            if (r.ok) {
              const info = (await r.json()) as { sizeBytes: number; fileName: string };
              const msg = `registrazione salvata sul server: ${info.fileName} (${(info.sizeBytes / 1_048_576).toFixed(1)} MB), scaricabile da "Chiamate"`;
              log(msg); setRecNotice(msg);
              return;
            }
            log(`chiusura registrazione rifiutata (${r.status}): salvo nei download`);
          } catch (e) { log(`chiusura registrazione fallita: ${e}; salvo nei download`); }
        }
        const name = `MDT-sessione-${peerName}-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-")}.webm`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
        const msg = `registrazione salvata nei download: ${name} (${(blob.size / 1_048_576).toFixed(1)} MB)`;
        log(msg); setRecNotice(msg);
      };
      recStartedAt.current = Date.now();
      rec.start(1000);
      recorder.current = rec;
      setRecording(true);
      log("registrazione avviata");
    } catch (e) { log(`registrazione non avviata: ${e}`); }
  }

  /** Ferma la registrazione (il file parte da solo); true se c'era una registrazione in corso. */
  function stopRecording(): boolean {
    const rec = recorder.current; recorder.current = null;
    setRecording(false);
    recAudio.current?.close().catch(() => {}); recAudio.current = null;
    if (!rec) return false;
    if (rec.state !== "inactive") rec.stop();
    return true;
  }

  function teardown(reason?: string) {
    const endedCall = callRef.current;
    const saved = stopRecording();
    setNetNotice(null);
    if (endedCall) setEnded({ peerName: endedCall.peerName, duration: endedCall.connectedAt ? Math.round((Date.now() - endedCall.connectedAt) / 1000) : 0, saved, reason });
    peer.current?.close(); peer.current = null;
    localStream.current?.getTracks().forEach((t) => t.stop()); localStream.current = null;
    if (remoteVideo.current) remoteVideo.current.srcObject = null;
    if (localVideo.current) localVideo.current.srcObject = null;
    setCallBoth(null); setIncoming(null); setArrows([]); setStats(null); setClock(null); setPeerState(""); setDocs([]); setElapsed(0);
  }

  // ---------------- frecce ----------------

  function onAnnotation(msg: AnnotationMessage) {
    if (msg.t === "arrow") setArrows((a) => [...a, { id: msg.id, x: msg.x, y: msg.y, mine: false, color: msg.color ?? ARROW_COLORS[1].hex }]);
    else if (msg.t === "clear") setArrows([]);
    else if (msg.t === "remove") setArrows((a) => a.filter((ar) => ar.id !== msg.id));
    else if (msg.t === "arrowpos") {
      // il visore riproietta le frecce ancorate nella sua vista: il punto segue la freccia reale (e sparisce se esce dal campo)
      const pos = new Map(msg.items.map((i) => [i.id, i]));
      setArrows((a) => a.map((ar) => { const p = pos.get(ar.id); return p ? { ...ar, x: p.x, y: p.y, visible: p.v, tracked: true } : ar; }));
    }
    else if (msg.t === "diag") log(`visore (${msg.platform}): camera ${msg.camera} · frame catturati ${msg.localFrames} · frame ricevuti ${msg.remoteFrames}${msg.codec ? ` · codec forzato ${msg.codec}` : ""}`);
  }

  function onVideoClick(e: React.MouseEvent<HTMLVideoElement>) {
    const v = e.currentTarget;
    if (!peer.current) return;
    // il video è in object-fit: contain → tolgo le bande nere; senza video (riquadro nero) uso tutto il riquadro
    const rect = v.getBoundingClientRect();
    const vw = v.videoWidth || rect.width, vh = v.videoHeight || rect.height;
    const scale = Math.min(rect.width / vw, rect.height / vh);
    const w = vw * scale, h = vh * scale;
    const ox = (rect.width - w) / 2, oy = (rect.height - h) / 2;
    const x = (e.clientX - rect.left - ox) / w, y = (e.clientY - rect.top - oy) / h;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;

    // tempo del frame mostrato: captureTime se il browser lo espone, altrimenti ora - RTT/2 - buffer
    const f = lastFrame.current;
    const frameTime = f.capture ? performance.timeOrigin + f.capture : Date.now() - (peer.current.rttMs / 2) - 100;
    const msg = peer.current.sendArrow(x, y, frameTime, arrowColor);
    if (msg.t === "arrow") {
      setArrows((a) => [...a, { id: msg.id, x, y, mine: true, color: arrowColor }]);
      log(`freccia (${x.toFixed(3)}, ${y.toFixed(3)}) t=${msg.ts} ${f.capture ? "[captureTime]" : "[stima RTT]"}`);
    }
  }

  /** Annulla l'ultima freccia messa da me (sul visore e qui). */
  function undoArrow() {
    const mine = arrows.filter((a) => a.mine);
    const last = mine[mine.length - 1];
    if (!last) return;
    peer.current?.send({ t: "remove", id: last.id });
    setArrows((a) => a.filter((ar) => ar.id !== last.id));
  }

  function clearArrows() { peer.current?.send({ t: "clear" }); setArrows([]); }

  function toggleMic() { const on = !micOn; setMicOn(on); localStream.current?.getAudioTracks().forEach((t) => (t.enabled = on)); }
  function toggleCam() { const on = !camOn; setCamOn(on); localStream.current?.getVideoTracks().forEach((t) => (t.enabled = on)); }

  // ---------------- documenti ----------------

  async function uploadFiles(files: FileList | File[]) {
    const c = callRef.current;
    if (!c) return;
    for (const file of Array.from(files)) {
      const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setDocs((d) => [...d, { id: tempId, fileName: file.name, pageCount: 0, status: "uploading" }]);
      try {
        const form = new FormData();
        form.append("file", file, file.name);
        const r = await fetch(`/api/calls/${c.callId}/documents`, { method: "POST", headers: authHeaders(session?.token ?? ""), body: form });
        if (!r.ok) {
          let msg = `HTTP ${r.status}`;
          try { msg = ((await r.json()) as { error?: string }).error ?? msg; } catch { /* corpo non JSON */ }
          throw new Error(msg);
        }
        const doc = (await r.json()) as { id: string; fileName: string; pageCount: number };
        setDocs((d) => d.map((x) => (x.id === tempId ? { id: doc.id, fileName: doc.fileName, pageCount: doc.pageCount, status: "ready" } : x)));
        log(`documento inviato: ${doc.fileName} (${doc.pageCount} pagine)`);
      } catch (e) {
        const err = (e as Error).message;
        setDocs((d) => d.map((x) => (x.id === tempId ? { ...x, status: "error", error: err } : x)));
        log(`documento non inviato: ${err}`);
      }
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault(); setDragging(false);
    if (e.dataTransfer.files.length) uploadFiles(e.dataTransfer.files);
  }

  // ---------------- effetti ----------------

  // suoneria (WebAudio, generata) finché c'è una chiamata in arrivo
  useEffect(() => {
    if (!incoming) return;
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctx();
    const gain = ctx.createGain(); gain.gain.value = 0.12; gain.connect(ctx.destination);
    let alive = true;
    const beep = (at: number, freq: number) => {
      const o = ctx.createOscillator(); o.type = "sine"; o.frequency.value = freq; o.connect(gain);
      o.start(at); o.stop(at + 0.18);
    };
    const cycle = () => {
      if (!alive) return;
      const t = ctx.currentTime;
      beep(t, 440); beep(t + 0.24, 554.4); beep(t + 0.56, 440); beep(t + 0.8, 554.4);
      timer = window.setTimeout(cycle, 2000);
    };
    let timer = 0; cycle();
    return () => { alive = false; window.clearTimeout(timer); ctx.close().catch(() => {}); };
  }, [incoming]);

  // timer della sessione
  useEffect(() => {
    if (!call?.connectedAt) return;
    const start = call.connectedAt;
    const id = window.setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [call?.connectedAt]);

  // tempo di cattura dei frame (requestVideoFrameCallback, Chrome/Edge)
  useEffect(() => {
    const v = remoteVideo.current as (HTMLVideoElement & { requestVideoFrameCallback?: (cb: (now: number, meta: { captureTime?: number }) => void) => number }) | null;
    if (!v?.requestVideoFrameCallback) return;
    let alive = true;
    const tick = (now: number, meta: { captureTime?: number }) => {
      lastFrame.current = { presented: now, capture: meta.captureTime };
      if (alive) v.requestVideoFrameCallback!(tick);
    };
    v.requestVideoFrameCallback(tick);
    return () => { alive = false; };
  }, [call?.callId]);

  // statistiche ogni 2 s durante la chiamata
  useEffect(() => {
    if (!call) return;
    const id = window.setInterval(async () => { if (peer.current) setStats(await readStats(peer.current.pc, statsPrev.current)); }, 2000);
    return () => window.clearInterval(id);
  }, [call?.callId]);

  // ---------------- UI ----------------

  // online in cima, poi in chiamata, poi offline; a parità ordine alfabetico (21/10/2026)
  const rank = (s: UserInfo["status"]) => (s === "online" ? 0 : s === "in_call" ? 1 : 2);
  const others = users.filter((u) => u.id !== me?.id).sort((a, b) => rank(a.status) - rank(b.status) || a.name.localeCompare(b.name, "it"));
  const fmtSeconds = (s: number) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const mmss = fmtSeconds(elapsed);
  const hasMine = arrows.some((a) => a.mine);

  const videoBox = (
    <div className="video-wrap">
      <video ref={remoteVideo} autoPlay playsInline onClick={onVideoClick} />
      {/* Nessun segno sul video: le frecce si vedono già nella vista del visore (richiesta del 14/10/2026).
          L'elenco `arrows` serve solo per Annulla / Cancella. */}
      {call && <div className="video-hint">Clicca sul video: la freccia appare nello spazio reale del tecnico</div>}
      {!call && <div className="hint"><p>Seleziona un utente online e premi <b>Chiama</b>, oppure attendi una chiamata.</p></div>}
      <div className="pip-wrap">
        <video ref={localVideo} className="pip" autoPlay playsInline muted />
        {!camOn && <div className="pip-off">La tua camera è spenta</div>}
      </div>
    </div>
  );

  const diagnostics = (
    <details className="diag">
      <summary>Diagnostica</summary>
      {call && <p><small>stato {call.state} · {peerState}{clock ? ` · RTT dati ${clock.rtt} ms · offset orologio ${clock.offset} ms` : ""}</small></p>}
      {stats && <p><small>ricevo {stats.codec || "?"} {stats.width}×{stats.height} @ {stats.fps} fps · {stats.kbps} kbit/s · frame decodificati {stats.framesDecoded} · RTT ICE {stats.rttMs} ms · {stats.candidate}<br />invio {stats.outCodec || "?"} · {stats.outKB} KB · frame codificati {stats.outFrames}</small></p>}
      <pre className="log">{logs.join("\n")}</pre>
    </details>
  );

  if (call) {
    return (
      <div className="app session" ref={sessionRoot}>
        <header className="session-head">
          <span className="rec" /> <b>Sessione con {call.peerName}</b>
          <span className="session-state">{call.state === "connected" ? "" : call.state === "ringing" ? "squillo…" : "connessione…"}</span>
          {netNotice && <span className="net-notice">{netNotice}</span>}
          <span className="timer">[{mmss}]</span>
          {document.fullscreenEnabled && <button className="fs" onClick={toggleFullscreen} title={fullscreen ? "Esci da schermo intero" : "Schermo intero"}>
            {fullscreen ? <Icon.compress /> : <Icon.expand />}<span>{fullscreen ? "Esci" : "Schermo intero"}</span>
          </button>}
        </header>

        <div className="session-body">
          <aside className="tools">
            <small>Strumenti</small>
            <button className="tool active" title="Clicca sul video per mettere una freccia"><Icon.arrow />Freccia</button>
            <button className="tool" onClick={undoArrow} disabled={!hasMine} title="Toglie l'ultima freccia che hai messo"><Icon.undo />Annulla</button>
            <button className="tool" onClick={clearArrows} disabled={arrows.length === 0} title="Toglie tutte le frecce"><Icon.trash />Cancella</button>
            <small>Colore</small>
            <div className="colors">
              {ARROW_COLORS.map((c) => (
                <button key={c.hex} className={`swatch ${arrowColor === c.hex ? "active" : ""}`} style={{ background: c.hex }} title={c.name} onClick={() => setArrowColor(c.hex)} />
              ))}
            </div>
          </aside>

          {videoBox}

          <aside className="docs">
            <h3>Documenti</h3>
            <div className={`dropzone ${dragging ? "over" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}>
              <div className="ico"><Icon.upload /></div>
              <p>Trascina qui PDF o immagini</p>
              <button className="outline" onClick={() => fileInput.current?.click()}>Scegli file</button>
              <input ref={fileInput} type="file" accept=".pdf,image/*" multiple hidden onChange={(e) => { if (e.target.files) uploadFiles(e.target.files); e.target.value = ""; }} />
            </div>
            <ul className="doclist">
              {docs.map((d) => (
                <li key={d.id} className={d.status}>
                  <span className="name" title={d.fileName}>{d.fileName}</span>
                  <small>{d.status === "uploading" ? "invio…" : d.status === "error" ? d.error : `${d.pageCount} pag. · sul visore`}</small>
                </li>
              ))}
              {docs.length === 0 && <li className="empty"><small>Nessun documento inviato</small></li>}
            </ul>
            {diagnostics}
          </aside>
        </div>

        <footer className="session-bar">
          <button className={`round rec ${recording ? "on" : ""}`} onClick={() => (recording ? stopRecording() : startRecording())} title={recording ? "Ferma la registrazione e salva il file" : "Registra la sessione (video del visore + audio)"}><Icon.rec /><span>{recording ? "Stop registrazione" : "Registra"}</span></button>
          <button className={`round ${micOn ? "" : "off"}`} onClick={toggleMic} title={micOn ? "Spegni microfono" : "Accendi microfono"}>{Icon.mic(!micOn)}<span>{micOn ? "Microfono" : "Microfono off"}</span></button>
          <button className={`round ${camOn ? "" : "off"}`} onClick={toggleCam} title={camOn ? "Spegni camera" : "Accendi camera"}>{Icon.cam(!camOn)}<span>{camOn ? "Camera" : "Camera off"}</span></button>
          <button className="danger end" onClick={hangUp}><Icon.hangup /><span>Termina</span></button>
        </footer>
      </div>
    );
  }

  if (!session) return <Login onLogin={onLogin} notice={loginNotice} />;

  return (
    <div className="app">
      <header>
        <h1>MDT Assistenza remota</h1>
        <div className="head-right">
          <div className={`conn ${connection}`}>{session.user.name} · {connection}</div>
          <button className="ghost" onClick={() => { setShowUsers(false); setShowHistory((v) => !v); }} title="Registro delle mie chiamate">{showHistory ? "Assistenza" : "Chiamate"}</button>
          {me?.isAdmin && <button className="ghost" onClick={() => { setShowHistory(false); setShowUsers((v) => !v); }} title="Gestione degli account">{showUsers ? "Assistenza" : "Utenti"}</button>}
          <button className="ghost" onClick={logout} title="Esci dall'account">Esci</button>
        </div>
      </header>

      {showHistory && !incoming ? (
        <main className="single"><CallHistory token={session.token} onBack={() => setShowHistory(false)} /></main>
      ) : showUsers && me?.isAdmin && !incoming ? (
        <main className="single"><Users token={session.token} meId={me.id} onBack={() => setShowUsers(false)} /></main>
      ) : (
      <main>
        <aside>
          {!me ? (
            <section className="card">
              <h2>Collegamento</h2>
              <p>{connection === "connecting" ? "Collegamento al server…" : "Server non raggiungibile."}</p>
              {connection === "disconnected" && <button className="primary" onClick={connect}>Riprova</button>}
            </section>
          ) : (
            <section className="card">
              <h2>Utenti HoloLens <button className="ghost" onClick={refreshUsers}>↻</button></h2>
              <ul className="users">
                {others.map((u) => (
                  <li key={u.id}>
                    <span className={`dot ${u.status}`} />
                    <span className="name">{u.name}<small>Utente HoloLens · {u.status === "online" ? "online" : u.status === "in_call" ? "in chiamata" : "offline"}</small></span>
                    <button className="primary" disabled={u.status !== "online"} onClick={() => callUser(u)}>Chiama</button>
                  </li>
                ))}
              </ul>
              <label className="check"><input type="checkbox" checked={camOn} onChange={(e) => setCamOn(e.target.checked)} /><span>Invia la mia webcam (da scegliere prima di rispondere)</span></label>
            </section>
          )}

          {ended && !incoming && (
            <section className="card ended">
              <h2>Chiamata terminata</h2>
              <p>con <b>{ended.peerName}</b><br />durata {fmtSeconds(ended.duration)}{ended.reason && <><br /><span className="reason">{ended.reason}</span></>}{ended.saved && <><br />{recNotice ?? "registrazione in salvataggio…"}</>}</p>
            </section>
          )}

          {incoming && (
            <section className="card incoming">
              <h2>Chiamata in arrivo</h2>
              <p><b>{incoming.from.name}</b><br />Utente HoloLens</p>
              <div className="row">
                <button className="success" onClick={accept}>Rispondi</button>
                <button className="danger" onClick={reject}>Rifiuta</button>
              </div>
            </section>
          )}
        </aside>

        <section className="stage">
          {videoBox}
          {diagnostics}
        </section>
      </main>
      )}
    </div>
  );
}
